import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAdminAuth } from "./auth/useAdminAuth";
import { useAdminLang } from "./hooks/useAdminLang";
import s from "./AdminLayout.module.css";

function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= bp : false,
  );
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= bp);
    window.addEventListener("resize", fn, { passive: true });
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return mobile;
}

export default function AdminLayout() {
  const { admin, logout, canManage, isSuperAdmin } = useAdminAuth();
  const { t, lang, setLang } = useAdminLang();
  const [cmsOpen, setCmsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => setDrawerOpen(false), [location]);

  // Prevent body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const n = t.nav;

  const NAV = [
    { label: n.dashboard, path: "/admin", icon: "⊞", end: true },
    { label: n.users, path: "/admin/users", icon: "👥" },
    { label: n.listings, path: "/admin/listings", icon: "📋" },
    { label: n.reports, path: "/admin/reports", icon: "🚩" },
    { label: n.orders, path: "/admin/orders", icon: "🧾" },
    ...(isSuperAdmin
      ? [{ label: "Admin Staff", path: "/admin/staff", icon: "🔐" }]
      : []),
  ];

  const CMS_NAV = [
    { label: n.heroSlides, path: "/admin/cms/hero" },
    { label: n.websiteListings, path: "/admin/cms/websitelistings" },
    { label: n.categories, path: "/admin/cms/categories" },
    { label: n.features, path: "/admin/cms/features" },
    { label: n.howItWorks, path: "/admin/cms/howitworks" },
    { label: n.faq, path: "/admin/cms/faq" },
    { label: n.pricing, path: "/admin/cms/pricing" },
    { label: n.aboutUs, path: "/admin/cms/about" },
    { label: n.terms, path: "/admin/cms/terms" },
    { label: n.privacy, path: "/admin/cms/privacy" },
  ];

  async function handleLogout() {
    await logout();
    navigate("/admin/login");
  }

  // Shared sidebar content — used both in static sidebar and mobile drawer
  function SidebarContent() {
    return (
      <>
        <div className={s.sidebarTop}>
          <div className={s.brand}>
            <span className={s.brandLogo}>B</span>
            <span className={s.brandName}>Bitakas</span>
            <span className={s.brandTag}>Admin</span>
          </div>

          <nav className={s.nav}>
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `${s.navItem} ${isActive ? s.navActive : ""}`
                }
              >
                <span className={s.navIcon}>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}

            {/* CMS editing is available to admin and super-admin roles. */}
            {canManage && <button
              className={s.navGroup}
              onClick={() => setCmsOpen((o) => !o)}
            >
              <span className={s.navIcon}>🌐</span>
              {n.websiteCMS}
              <span className={`${s.chevron} ${cmsOpen ? s.open : ""}`}>›</span>
            </button>}

            {canManage && cmsOpen && (
              <div className={s.subNav}>
                {CMS_NAV.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `${s.subNavItem} ${isActive ? s.navActive : ""}`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </nav>
        </div>

        <div className={s.sidebarBottom}>
          {/* Language toggle */}
          <button
            onClick={() => setLang(lang === "en" ? "tr" : "en")}
            style={{
              width: "100%",
              marginBottom: 10,
              padding: "7px 12px",
              background: "rgba(255,255,255,.06)",
              color: "rgba(255,255,255,.7)",
              border: "1px solid rgba(255,255,255,.1)",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {lang === "en" ? "🇹🇷 Türkçe'ye Geç" : "🇬🇧 Switch to English"}
          </button>

          <div className={s.adminInfo}>
            <div className={s.adminAvatar}>
              {admin?.name?.charAt(0) ?? admin?.email?.charAt(0) ?? "A"}
            </div>
            <div style={{ overflow: "hidden" }}>
              <p
                className={s.adminName}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {admin?.name ?? "Admin"}
              </p>
              <p
                className={s.adminEmail}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {admin?.email}
              </p>
              <p className={s.adminEmail} style={{ textTransform: "capitalize" }}>
                {admin?.role?.replace("_", " ")}
              </p>
            </div>
          </div>
          <button className={s.logoutBtn} onClick={handleLogout}>
            {n.signOut}
          </button>
        </div>
      </>
    );
  }

  return (
    <div className={s.shell}>
      {/* ── Desktop static sidebar ─────────────────────────────────────── */}
      <aside className={s.sidebar}>
        <SidebarContent />
      </aside>

      {/* ── Mobile overlay ─────────────────────────────────────────────── */}
      <div
        className={`${s.overlay} ${drawerOpen ? s.overlayVisible : ""}`}
        onClick={() => setDrawerOpen(false)}
      />

      {/* ── Mobile sidebar drawer ──────────────────────────────────────── */}
      <aside
        className={`${s.sidebarDrawer} ${drawerOpen ? s.sidebarDrawerOpen : ""}`}
      >
        <SidebarContent />
      </aside>

      {/* ── Main area ──────────────────────────────────────────────────── */}
      <div className={s.main}>
        {/* Mobile top bar */}
        <div className={s.topBar}>
          <div className={s.topBarBrand}>
            <div
              className={s.brandLogo}
              style={{ width: 28, height: 28, fontSize: 13 }}
            >
              B
            </div>
            <span
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: 15,
                letterSpacing: 1,
              }}
            >
              Bitakas Admin
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={() => setLang(lang === "en" ? "tr" : "en")}
              style={{
                background: "rgba(255,255,255,.1)",
                border: "none",
                color: "#fff",
                borderRadius: 6,
                padding: "4px 8px",
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {lang === "en" ? "🇹🇷" : "🇬🇧"}
            </button>
            <button
              className={s.menuBtn}
              onClick={() => setDrawerOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              <span
                className={`${s.menuBar} ${drawerOpen ? s.menuBar1Open : ""}`}
              />
              <span
                className={`${s.menuBar} ${drawerOpen ? s.menuBarHide : ""}`}
              />
              <span
                className={`${s.menuBar} ${drawerOpen ? s.menuBar3Open : ""}`}
              />
            </button>
          </div>
        </div>

        {!canManage && (
          <div className={s.readOnlyBanner}>
            Viewer access is read-only. Changes require an admin role.
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
}
